import React, { FC, useEffect, useState, useMemo } from "react";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { usePurchaseEnquiryModel } from "../purchaseEnquiryModel";
import { IPurchaseEnquiryFields } from "../purchaseEnquiryType"
import { momentFormat, formatNum } from "@/utils/utils";
import { useDictModel } from "@/views/base/dict/dictModel";
import YhTag from "@/components/YhTag";
import useNavModel from "@/model/navModel";
import { tableSize } from "@/utils/constants";
import YhTootip from "@/components/YhTootip";
import { roleColumnModel } from "@/utils/roleColumn";
import useGlobalModel from "@/model/globalModel";

type statusType = {
    "purchase-enquiry.transfer": string,
    "purchase-enquiry.create": string,
    [key: string]: any
}

export function DataTable() {

    const {
        dataSource, selectedRowKeys, pgCfg, descRows, descTotal, rowsDescTotal,
        loadData, setSelectedRowKeys, setDescRows, reset
    } = usePurchaseEnquiryModel();
    const { getTreeNodeName } = useDictModel();
    const { selectedNavId, replaceNav, findNav } = useNavModel();
    const { user: { roles } } = useGlobalModel();
    const { getRoleColumns } = roleColumnModel();
    const [columns, setColumns] = useState<ColumnsType<IPurchaseEnquiryFields>>();

    const onLook = (code: string) => {
        let supplierEditKey = "11-6-3";
        replaceNav(
            selectedNavId,
            supplierEditKey,
            { id: code, mode: "look", },
            true
        );
    }
    useEffect(() => {
        loadData();
    }, [])

    const statusColor: any = {
        "purchase_enquiry.init": "blue",
        "purchase_enquiry.end": "cyan",
        "purchase_enquiry.underway": "orange",
        "purchase_enquiry.audited": "green",
        "purchase_enquiry.reject": "red"
    }
    const defaultColumns: ColumnsType<IPurchaseEnquiryFields> = [
        {
            title: '序号',
            fixed: "left",
            width: 60,
            render: (t, r, i) => `${i + 1}`,
        }, {
            title: '询价单号',
            width: 140,
            dataIndex: 'code',
            render: (text: any, record: any,) => <YhTootip text={text} style={{ width: 140 }} onClick={() => onLook(record.id)} />,
        }, {
            title: '询价标题',
            width: 300,
            dataIndex: 'title',
            render: (text: any, record: any,) => <YhTootip text={text} style={{ width: 300 }} />,
        }, {
            title: '单据状态',
            width: 80,
            dataIndex: 'status',
            render: (text) => <YhTag color={statusColor[text]} text={getTreeNodeName(text)} />,
        }, {
            title: '审批类型',
            width: 100,
            dataIndex: 'approveTypeName',
            render: (text) => <Tooltip placement="topLeft" style={{ width: 100 }} title={text}>{text}</Tooltip>,
        }, {
            title: '询价类型',
            width: 120,
            dataIndex: 'type',
            render: (text) => getTreeNodeName(text),
        }, {
            title: '创建人',
            width: 100,
            dataIndex: 'createUser',
        }, {
            title: '创建时间',
            width: 150,
            dataIndex: 'createTime',
            render: (text) => momentFormat(text)

        }, {
            title: '发布人',
            width: 100,
            dataIndex: 'sendUser',
        }, {
            title: '发布时间',
            width: 150,
            dataIndex: 'sendTime',
            render: (text) => momentFormat(text)

        }, {
            title: '审批人',
            width: 100,
            dataIndex: 'approver',
        }, {
            title: '审批时间',
            width: 140,
            dataIndex: 'approvalTime',
            render: (text) => momentFormat(text)
        }, {
            title: '报价截止时间',
            width: 140,
            dataIndex: 'endTime',
            render: (text) => momentFormat(text)
        }, {
            title: '备注',
            width: 140,
            dataIndex: 'note',
        },
        {
            title: '所属站点',
            width: 250,
            fixed: "right",
            dataIndex: 'shopName',
            render: (text: any) => <YhTootip text={text} style={{ width: 250 }} />,
        },
    ];

    const roleColumns = useMemo(() => {
        //根据可视化配置处理需要展示相关列
        const params = { "code": "purchaseEnquiry", "roleCodes": roles };
        return getRoleColumns("purchaseEnquiry", "主单", defaultColumns, params);
    }, [roles]);
    //点击行选高亮
    const onRow = (record: IPurchaseEnquiryFields) => {
        let { id } = record as { id: string }
        let findIndexDesc = descRows.findIndex((item) => (item.id === id))
        let findIndexRowKeys = selectedRowKeys.findIndex((item) => (item === id))

        let newDescRows = [...descRows];
        let newSelectedRowKeys = [...selectedRowKeys];
        console.log(findIndexDesc)
        if (findIndexDesc === -1) {
            //统计数据
            newDescRows.push(record)
            setDescRows(newDescRows)
            //表格选中行Key值
            newSelectedRowKeys.push(id);
            setSelectedRowKeys(newSelectedRowKeys);
        } else {
            //统计数据
            newDescRows.splice(findIndexDesc, 1);
            setDescRows(newDescRows);
            //表格选中行Key值
            newSelectedRowKeys.splice(findIndexDesc, 1);
            setSelectedRowKeys(newSelectedRowKeys);
        }
    };
    const onRightClick = (record: IPurchaseEnquiryFields) => {
        setDescRows([record])
        setSelectedRowKeys([record.id as string])
    }
    return (
        <Table
            bordered
            style={{ cursor: "pointer" }}
            size={tableSize}
            rowKey={record => record.id as string}
            rowSelection={{
                type: "checkbox",
                onChange: (selectedRowKeys: React.Key[], selectedRows: IPurchaseEnquiryFields[]) => {
                    setSelectedRowKeys(selectedRowKeys as string[]);
                    setDescRows(selectedRows)
                },
                selectedRowKeys: selectedRowKeys
            }}
            columns={roleColumns}
            dataSource={dataSource}
            pagination={{
                showSizeChanger: true,
                ...pgCfg,
                defaultPageSize: 25,
                pageSizeOptions: ['15', '25', '50', '100', '200', '500'],
                position: ["bottomRight"],
                showTotal: () => `共${pgCfg.total}条`,
                onChange: (pageNum, pageSize) => {
                    loadData({ pageNum, pageSize }).then();
                }
            }}
            onRow={record => ({
                onClick: () => onRow(record),
                onContextMenu: (e) => {
                    e.preventDefault();
                    onRightClick(record)
                }

            })}
            scroll={{ x: 'max-content', y: 'calc(100vh - 370px)' }}

        />
    )
}
